<script setup lang="ts">
import { onMounted, onUnmounted, useTemplateRef, watch } from 'vue';
import { useRoute } from 'vue-router';

import AddAddressDialog from '@/components/dialogs/AddAddressDialog.vue';
import AddressBookAddressDialog from '@/components/dialogs/AddressBookAddressDialog.vue';
import AddressBookDeleteAddressDialog from '@/components/dialogs/AddressBookDeleteAddressDialog.vue';
import AdvancedSearchMultiSearchDialog from '@/components/dialogs/AdvancedSearchMultiSearchDialog.vue';
import ArrivedDepartedStopDialog from '@/components/dialogs/ArrivedDepartedStopDialog.vue';
import CancelCloseStopDialog from '@/components/dialogs/CancelCloseStopDialog.vue';
import ChooseOptionStopDialog from '@/components/dialogs/ChooseOptionStopDialog.vue';
import CloneJobDialog from '@/components/dialogs/CloneJobDialog.vue';
import ConfirmScrapBookingDialog from '@/components/dialogs/ConfirmScrapBookingDialog.vue';
import DeleteJobPhotoDialog from '@/components/dialogs/DeleteJobPhotoDialog.vue';
import DetailedJobPhotoDialog from '@/components/dialogs/DetailedJobPhotoDialog.vue';
import DispatchAssignDriverDialog from '@/components/dialogs/DispatchAssignDriverDialog.vue';
import DispatchConfirmDialog from '@/components/dialogs/DispatchConfirmDialog.vue';
import DriverReportGenerateDialog from '@/components/dialogs/DriverReportGenerateDialog.vue';
import EditTimestampsDialog from '@/components/dialogs/EditTimestampsDialog.vue';
import JobEditCancelJobDialog from '@/components/dialogs/JobEditCancelJobDialog.vue';
import JobEditChangeCustomerCodeDialog from '@/components/dialogs/JobEditChangeCustomerCodeDialog.vue';
import JobEditCloseAsFutileDialog from '@/components/dialogs/JobEditCloseAsFutileDialog.vue';
import JobEditDeleteStopDialog from '@/components/dialogs/JobEditDeleteStopDialog.vue';
import JobEditErrorDialog from '@/components/dialogs/JobEditErrorDialog.vue';
import JobEditLockJobDialog from '@/components/dialogs/JobEditLockJobDialog.vue';
import JobEditLockTimeoutDialog from '@/components/dialogs/JobEditLockTimeoutDialog.vue';
import JobEditLockedByOtherDialog from '@/components/dialogs/JobEditLockedByOtherDialog.vue';
import JobEditNotFoundDialog from '@/components/dialogs/JobEditNotFoundDialog.vue';
import JobEditSplitToBaseDialog from '@/components/dialogs/JobEditSplitToBaseDialog.vue';
import JobEditUndoAllChangesDialog from '@/components/dialogs/JobEditUndoAllChangesDialog.vue';
import KpiReportGenerateDialog from '@/components/dialogs/KpiReportGenerateDialog.vue';
import LogoutDialog from '@/components/dialogs/LogoutDialog.vue';
import NewAirRoadUnpackDialog from '@/components/dialogs/NewAirRoadUnpackDialog.vue';
import NewMessageChatDialog from '@/components/dialogs/NewMessageChatDialog.vue';
import NewQueryDialog from '@/components/dialogs/NewQueryDialog.vue';
import NewRFCDialog from '@/components/dialogs/NewRFCDialog.vue';
import NewTicketDialog from '@/components/dialogs/NewTicketDialog.vue';
import PrintDialog from '@/components/dialogs/PrintDialog.vue';
import ReturnLabelsPrintDialog from '@/components/dialogs/ReturnLabelsPrintDialog.vue';
import SaveJobDraftDialog from '@/components/dialogs/SaveJobDraftDialog.vue';
import SavedFiltersDialog from '@/components/dialogs/SavedFiltersDialog.vue';
import SavingCurrentSearchDialog from '@/components/dialogs/SavingCurrentSearchDialog.vue';
import SplitToBaseDialog from '@/components/dialogs/SplitToBaseDialog.vue';
import UploadingFilesDialog from '@/components/dialogs/UploadingFilesDialog.vue';
import { useDialogStore } from '@/stores/useDialogStore';

const dialogStore = useDialogStore();

const dispatchAssignDriverRef = useTemplateRef('dispatchAssignDriver');
const dispatchConfirmRef = useTemplateRef('dispatchConfirm');
const arrivedDepartedStopRef = useTemplateRef('arrivedDepartedStop');
const chooseOptionStopDialogRef = useTemplateRef('chooseOptionStopDialog');
const cancelCloseStopRef = useTemplateRef('cancelCloseStop');
const newTicketRef = useTemplateRef('newTicket');
const newRFCRef = useTemplateRef('newRFC');
const cloneJobRef = useTemplateRef('cloneJob');
const newQueryRef = useTemplateRef('newQuery');
const jobEditCancelJobRef = useTemplateRef('jobEditCancelJob');
const jobEditChangeCustomerCodeRef = useTemplateRef(
  'jobEditChangeCustomerCode',
);
const jobEditCloseAsFutileRef = useTemplateRef('jobEditCloseAsFutile');
const jobEditDeleteStopRef = useTemplateRef('jobEditDeleteStop');
const jobEditErrorRef = useTemplateRef('jobEditError');
const jobEditLockedByOtherRef = useTemplateRef('jobEditLockedByOther');
const jobEditLockTimeoutRef = useTemplateRef('jobEditLockTimeout');
const jobEditNotFoundRef = useTemplateRef('jobEditNotFound');
const jobEditSplitToBaseRef = useTemplateRef('jobEditSplitToBase');
const jobEditLockJobRef = useTemplateRef('jobEditLockJob');
const jobEditUndoAllChangesRef = useTemplateRef('jobEditUndoAllChanges');
const splitToBaseRef = useTemplateRef('splitToBase');
const editTimestampsRef = useTemplateRef('editTimestamps');
const advancedSearchMultiSearchRef = useTemplateRef(
  'advancedSearchMultiSearch',
);
const savedFiltersRef = useTemplateRef('savedFilters');
const savingCurrentSearchRef = useTemplateRef('savingCurrentSearch');
const uploadingFilesRef = useTemplateRef('uploadingFiles');
const detailedJobPhotoRef = useTemplateRef('detailedJobPhoto');
const deleteJobPhotoRef = useTemplateRef('deleteJobPhoto');
const printRef = useTemplateRef('print');
const returnLabelsPrintRef = useTemplateRef('returnLabelsPrint');
const newAirRoadUnpackRef = useTemplateRef('newAirRoadUnpack');
const newMessageChatRef = useTemplateRef('newMessageChat');
const kpiReportGenerateRef = useTemplateRef('kpiReportGenerate');
const driverReportGenerateRef = useTemplateRef('driverReportGenerate');
const addAddressRef = useTemplateRef('addAddress');
const addressBookAddressRef = useTemplateRef('addressBookAddress');
const addressBookDeleteAddressRef = useTemplateRef('addressBookDeleteAddress');
const saveJobDraftRef = useTemplateRef('saveJobDraft');
const confirmScrapBookingRef = useTemplateRef('confirmScrapBooking');
const logoutRef = useTemplateRef('logout');

export type DialogElement = typeof dispatchConfirmRef.value;

onMounted(() => {
  dialogStore.setDialogElement(
    'dispatchAssignDriver',
    dispatchAssignDriverRef.value,
  );
  dialogStore.setDialogElement('dispatchConfirm', dispatchConfirmRef.value);
  dialogStore.setDialogElement(
    'arrivedDepartedStop',
    arrivedDepartedStopRef.value,
  );
  dialogStore.setDialogElement(
    'chooseOptionStop',
    chooseOptionStopDialogRef.value,
  );
  dialogStore.setDialogElement('cancelCloseStop', cancelCloseStopRef.value);
  dialogStore.setDialogElement('newTicket', newTicketRef.value);
  dialogStore.setDialogElement('newRFC', newRFCRef.value);
  dialogStore.setDialogElement('cloneJob', cloneJobRef.value);
  dialogStore.setDialogElement('newQuery', newQueryRef.value);
  dialogStore.setDialogElement('jobEditCancelJob', jobEditCancelJobRef.value);
  dialogStore.setDialogElement(
    'jobEditChangeCustomerCode',
    jobEditChangeCustomerCodeRef.value,
  );
  dialogStore.setDialogElement(
    'jobEditCloseAsFutile',
    jobEditCloseAsFutileRef.value,
  );
  dialogStore.setDialogElement('jobEditDeleteStop', jobEditDeleteStopRef.value);
  dialogStore.setDialogElement('jobEditError', jobEditErrorRef.value);
  dialogStore.setDialogElement(
    'jobEditLockedByOther',
    jobEditLockedByOtherRef.value,
  );
  dialogStore.setDialogElement(
    'jobEditLockTimeout',
    jobEditLockTimeoutRef.value,
  );
  dialogStore.setDialogElement('jobEditNotFound', jobEditNotFoundRef.value);
  dialogStore.setDialogElement(
    'jobEditSplitToBase',
    jobEditSplitToBaseRef.value,
  );
  dialogStore.setDialogElement('jobEditLockJob', jobEditLockJobRef.value);
  dialogStore.setDialogElement(
    'jobEditUndoAllChanges',
    jobEditUndoAllChangesRef.value,
  );
  dialogStore.setDialogElement('splitToBase', splitToBaseRef.value);
  dialogStore.setDialogElement('editTimestamps', editTimestampsRef.value);
  dialogStore.setDialogElement(
    'advancedSearchMultiSearch',
    advancedSearchMultiSearchRef.value,
  );
  dialogStore.setDialogElement('savedFilters', savedFiltersRef.value);
  dialogStore.setDialogElement(
    'savingCurrentSearch',
    savingCurrentSearchRef.value,
  );
  dialogStore.setDialogElement('uploadingFiles', uploadingFilesRef.value);
  dialogStore.setDialogElement('detailedJobPhoto', detailedJobPhotoRef.value);
  dialogStore.setDialogElement('deleteJobPhoto', deleteJobPhotoRef.value);
  dialogStore.setDialogElement('print', printRef.value);
  dialogStore.setDialogElement('returnLabelsPrint', returnLabelsPrintRef.value);
  dialogStore.setDialogElement('newAirRoadUnpack', newAirRoadUnpackRef.value);
  dialogStore.setDialogElement('newMessageChat', newMessageChatRef.value);
  dialogStore.setDialogElement('kpiReportGenerate', kpiReportGenerateRef.value);
  dialogStore.setDialogElement(
    'driverReportGenerate',
    driverReportGenerateRef.value,
  );
  dialogStore.setDialogElement('addAddress', addAddressRef.value);
  dialogStore.setDialogElement(
    'addressBookAddress',
    addressBookAddressRef.value,
  );
  dialogStore.setDialogElement(
    'addressBookDeleteAddress',
    addressBookDeleteAddressRef.value,
  );
  dialogStore.setDialogElement('saveJobDraft', saveJobDraftRef.value);
  dialogStore.setDialogElement(
    'confirmScrapBooking',
    confirmScrapBookingRef.value,
  );
  dialogStore.setDialogElement('logout', logoutRef.value);
});

onUnmounted(() => {
  dialogStore.setDialogElement('dispatchAssignDriver', null);
  dialogStore.setDialogElement('dispatchConfirm', null);
  dialogStore.setDialogElement('arrivedDepartedStop', null);
  dialogStore.setDialogElement('chooseOptionStop', null);
  dialogStore.setDialogElement('cancelCloseStop', null);
  dialogStore.setDialogElement('newTicket', null);
  dialogStore.setDialogElement('newRFC', null);
  dialogStore.setDialogElement('cloneJob', null);
  dialogStore.setDialogElement('newQuery', null);
  dialogStore.setDialogElement('jobEditCancelJob', null);
  dialogStore.setDialogElement('jobEditChangeCustomerCode', null);
  dialogStore.setDialogElement('jobEditCloseAsFutile', null);
  dialogStore.setDialogElement('jobEditDeleteStop', null);
  dialogStore.setDialogElement('jobEditError', null);
  dialogStore.setDialogElement('jobEditLockedByOther', null);
  dialogStore.setDialogElement('jobEditNotFound', null);
  dialogStore.setDialogElement('jobEditLockTimeout', null);
  dialogStore.setDialogElement('jobEditSplitToBase', null);
  dialogStore.setDialogElement('jobEditLockJob', null);
  dialogStore.setDialogElement('jobEditUndoAllChanges', null);
  dialogStore.setDialogElement('splitToBase', null);
  dialogStore.setDialogElement('editTimestamps', null);
  dialogStore.setDialogElement('advancedSearchMultiSearch', null);
  dialogStore.setDialogElement('savedFilters', null);
  dialogStore.setDialogElement('savingCurrentSearch', null);
  dialogStore.setDialogElement('uploadingFiles', null);
  dialogStore.setDialogElement('detailedJobPhoto', null);
  dialogStore.setDialogElement('deleteJobPhoto', null);
  dialogStore.setDialogElement('print', null);
  dialogStore.setDialogElement('returnLabelsPrint', null);
  dialogStore.setDialogElement('newAirRoadUnpack', null);
  dialogStore.setDialogElement('newMessageChat', null);
  dialogStore.setDialogElement('kpiReportGenerate', null);
  dialogStore.setDialogElement('driverReportGenerate', null);
  dialogStore.setDialogElement('addAddress', null);
  dialogStore.setDialogElement('addressBookAddress', null);
  dialogStore.setDialogElement('addressBookDeleteAddress', null);
  dialogStore.setDialogElement('saveJobDraft', null);
  dialogStore.setDialogElement('confirmScrapBooking', null);
  dialogStore.setDialogElement('logout', null);
});

const route = useRoute();

watch(
  route,
  async function (newValues, oldValues) {
    // TOOD maybe all dialog should be included
    // TODO recheck the behavior with back/forward navigations
    void dialogStore.close('advancedSearchMultiSearch');
    void dialogStore.close('savedFilters');
    void dialogStore.close('savingCurrentSearch');
  },
  { immediate: true },
);
</script>

<template>
  <v-dialog
    ref="logout"
    v-model="dialogStore.logout.isDialog"
    :persistent="dialogStore.logout.dialogData.isConfirmLoading"
    max-width="350"
    width="auto"
  >
    <LogoutDialog
      v-bind="dialogStore.logout.dialogData"
      @cancel-btn-click="dialogStore.handleCancelClick($event, 'logout')"
      @confirm-btn-click="dialogStore.handleLogoutDialogConfirmClick"
    />
  </v-dialog>
  <v-dialog
    ref="dispatchAssignDriver"
    v-model="dialogStore.dispatchAssignDriver.isDialog"
    :persistent="dialogStore.dispatchAssignDriver.dialogData.isConfirmLoading"
    width="auto"
  >
    <DispatchAssignDriverDialog
      v-bind="dialogStore.dispatchAssignDriver.dialogData"
      @cancel-btn-click="
        dialogStore.handleCancelClick($event, 'dispatchAssignDriver')
      "
      @confirm-btn-click="
        dialogStore.handleDispatchAssignDriverDialogConfirmClick
      "
    />
  </v-dialog>
  <v-dialog
    ref="dispatchConfirm"
    v-model="dialogStore.dispatchConfirm.isDialog"
    :persistent="dialogStore.dispatchConfirm.dialogData.isConfirmLoading"
    width="auto"
  >
    <DispatchConfirmDialog
      v-bind="dialogStore.dispatchConfirm.dialogData"
      @cancel-btn-click="
        dialogStore.handleCancelClick($event, 'dispatchConfirm')
      "
      @confirm-btn-click="dialogStore.handleDispatchConfirmDialogConfirmClick"
    />
  </v-dialog>
  <v-dialog
    ref="arrivedDepartedStop"
    v-model="dialogStore.arrivedDepartedStop.isDialog"
    :persistent="dialogStore.arrivedDepartedStop.dialogData.isConfirmLoading"
    width="auto"
  >
    <ArrivedDepartedStopDialog
      v-bind="dialogStore.arrivedDepartedStop.dialogData"
      @cancel-btn-click="
        dialogStore.handleCancelClick($event, 'arrivedDepartedStop')
      "
      @confirm-btn-click="
        dialogStore.handleArrivedDepartedStopDialogConfirmClick
      "
    />
  </v-dialog>
  <v-dialog
    ref="chooseOptionStopDialog"
    v-model="dialogStore.chooseOptionStop.isDialog"
    :persistent="dialogStore.chooseOptionStop.dialogData.isConfirmLoading"
    width="auto"
  >
    <ChooseOptionStopDialog
      v-bind="dialogStore.chooseOptionStop.dialogData"
      @cancel-btn-click="
        dialogStore.handleCancelClick($event, 'chooseOptionStop')
      "
      @confirm-btn-click="dialogStore.handleChooseOptionStopDialogConfirmClick"
    />
  </v-dialog>
  <v-dialog
    ref="cancelCloseStop"
    v-model="dialogStore.cancelCloseStop.isDialog"
    :persistent="dialogStore.cancelCloseStop.dialogData.isConfirmLoading"
    width="auto"
  >
    <CancelCloseStopDialog
      v-bind="dialogStore.cancelCloseStop.dialogData"
      @cancel-btn-click="
        dialogStore.handleCancelClick($event, 'cancelCloseStop')
      "
      @confirm-btn-click="dialogStore.handleCancelCloseStopDialogConfirmClick"
    />
  </v-dialog>
  <v-dialog
    ref="newTicket"
    v-model="dialogStore.newTicket.isDialog"
    :persistent="dialogStore.newTicket.dialogData.isConfirmLoading"
    width="auto"
  >
    <NewTicketDialog
      v-bind="dialogStore.newTicket.dialogData"
      @cancel-btn-click="dialogStore.handleCancelClick($event, 'newTicket')"
      @confirm-btn-click="dialogStore.handleNewTicketDialogConfirmClick"
    />
  </v-dialog>
  <v-dialog
    ref="newRFC"
    v-model="dialogStore.newRFC.isDialog"
    :persistent="dialogStore.newRFC.dialogData.isConfirmLoading"
    width="auto"
  >
    <NewRFCDialog
      v-bind="dialogStore.newRFC.dialogData"
      @cancel-btn-click="dialogStore.handleCancelClick($event, 'newRFC')"
      @confirm-btn-click="dialogStore.handleNewRFCDialogConfirmClick"
    />
  </v-dialog>
  <v-dialog
    ref="cloneJob"
    v-model="dialogStore.cloneJob.isDialog"
    :persistent="dialogStore.cloneJob.dialogData.isConfirmLoading"
    width="auto"
  >
    <CloneJobDialog
      v-bind="dialogStore.cloneJob.dialogData"
      @cancel-btn-click="dialogStore.handleCancelClick($event, 'cloneJob')"
      @confirm-btn-click="dialogStore.handleCloneJobDialogConfirmClick"
    />
  </v-dialog>
  <v-dialog
    ref="newQuery"
    v-model="dialogStore.newQuery.isDialog"
    :persistent="dialogStore.newQuery.dialogData.isConfirmLoading"
    width="auto"
  >
    <NewQueryDialog
      v-bind="dialogStore.newQuery.dialogData"
      @cancel-btn-click="dialogStore.handleCancelClick($event, 'newQuery')"
      @confirm-btn-click="dialogStore.handleNewQueryDialogConfirmClick"
    />
  </v-dialog>
  <v-dialog
    ref="jobEditCancelJob"
    v-model="dialogStore.jobEditCancelJob.isDialog"
    :persistent="dialogStore.jobEditCancelJob.dialogData.isConfirmLoading"
    width="auto"
  >
    <JobEditCancelJobDialog
      v-bind="dialogStore.jobEditCancelJob.dialogData"
      @cancel-btn-click="
        dialogStore.handleCancelClick($event, 'jobEditCancelJob')
      "
      @confirm-btn-click="dialogStore.handleJobEditCancelJobDialogConfirmClick"
    />
  </v-dialog>
  <v-dialog
    ref="jobEditChangeCustomerCode"
    v-model="dialogStore.jobEditChangeCustomerCode.isDialog"
    :persistent="
      dialogStore.jobEditChangeCustomerCode.dialogData.isConfirmLoading
    "
    width="auto"
  >
    <JobEditChangeCustomerCodeDialog
      v-bind="dialogStore.jobEditChangeCustomerCode.dialogData"
      @cancel-btn-click="
        dialogStore.handleCancelClick($event, 'jobEditChangeCustomerCode')
      "
      @confirm-btn-click="
        dialogStore.handleJobEditChangeCustomerCodeDialogConfirmClick
      "
    />
  </v-dialog>
  <v-dialog
    ref="jobEditCloseAsFutile"
    v-model="dialogStore.jobEditCloseAsFutile.isDialog"
    :persistent="dialogStore.jobEditCloseAsFutile.dialogData.isConfirmLoading"
    width="auto"
  >
    <JobEditCloseAsFutileDialog
      v-bind="dialogStore.jobEditCloseAsFutile.dialogData"
      @cancel-btn-click="
        dialogStore.handleCancelClick($event, 'jobEditCloseAsFutile')
      "
      @confirm-btn-click="
        dialogStore.handleJobEditCloseAsFutileDialogConfirmClick
      "
    />
  </v-dialog>
  <v-dialog
    ref="jobEditDeleteStop"
    v-model="dialogStore.jobEditDeleteStop.isDialog"
    :persistent="dialogStore.jobEditDeleteStop.dialogData.isConfirmLoading"
    width="auto"
  >
    <JobEditDeleteStopDialog
      v-bind="dialogStore.jobEditDeleteStop.dialogData"
      @cancel-btn-click="
        dialogStore.handleCancelClick($event, 'jobEditDeleteStop')
      "
      @confirm-btn-click="dialogStore.handleJobEditDeleteStopDialogConfirmClick"
    />
  </v-dialog>
  <!-- max-width="600" -->
  <v-dialog
    ref="jobEditLockTimeout"
    v-model="dialogStore.jobEditLockTimeout.isDialog"
    :persistent="dialogStore.jobEditLockTimeout.dialogData.isConfirmLoading"
    width="auto"
  >
    <JobEditLockTimeoutDialog
      v-bind="dialogStore.jobEditLockTimeout.dialogData"
      @cancel-btn-click="
        dialogStore.handleCancelClick($event, 'jobEditLockTimeout')
      "
      @continue-btn-click="
        dialogStore.handleJobEditLockTimeoutDialogContinueClick
      "
    />
  </v-dialog>
  <v-dialog
    ref="jobEditSplitToBase"
    v-model="dialogStore.jobEditSplitToBase.isDialog"
    :persistent="dialogStore.jobEditSplitToBase.dialogData.isConfirmLoading"
    width="auto"
  >
    <JobEditSplitToBaseDialog
      v-bind="dialogStore.jobEditSplitToBase.dialogData"
      @cancel-btn-click="
        dialogStore.handleCancelClick($event, 'jobEditSplitToBase')
      "
      @confirm-btn-click="
        dialogStore.handleJobEditSplitToBaseDialogConfirmClick
      "
    />
  </v-dialog>
  <v-dialog
    ref="jobEditLockJob"
    v-model="dialogStore.jobEditLockJob.isDialog"
    :persistent="dialogStore.jobEditLockJob.dialogData.isConfirmLoading"
    width="auto"
  >
    <JobEditLockJobDialog
      v-bind="dialogStore.jobEditLockJob.dialogData"
      @cancel-btn-click="
        dialogStore.handleCancelClick($event, 'jobEditLockJob')
      "
      @confirm-btn-click="dialogStore.handleJobEditLockJobDialogConfirmClick"
    />
  </v-dialog>
  <v-dialog
    ref="jobEditUndoAllChanges"
    v-model="dialogStore.jobEditUndoAllChanges.isDialog"
    :persistent="dialogStore.jobEditUndoAllChanges.dialogData.isConfirmLoading"
    width="auto"
  >
    <JobEditUndoAllChangesDialog
      v-bind="dialogStore.jobEditUndoAllChanges.dialogData"
      @cancel-btn-click="
        dialogStore.handleCancelClick($event, 'jobEditUndoAllChanges')
      "
      @confirm-btn-click="
        dialogStore.handleJobEditUndoAllChangesDialogConfirmClick
      "
    />
  </v-dialog>
  <v-dialog
    ref="jobEditLockedByOther"
    v-model="dialogStore.jobEditLockedByOther.isDialog"
    persistent
    width="auto"
  >
    <JobEditLockedByOtherDialog
      v-bind="dialogStore.jobEditLockedByOther.dialogData"
      @okay-btn-click="dialogStore.close('jobEditLockedByOther')"
    />
  </v-dialog>
  <v-dialog
    ref="jobEditNotFound"
    v-model="dialogStore.jobEditNotFound.isDialog"
    persistent
    width="auto"
  >
    <JobEditNotFoundDialog
      v-bind="dialogStore.jobEditNotFound.dialogData"
      @okay-btn-click="dialogStore.close('jobEditNotFound')"
    />
  </v-dialog>
  <v-dialog
    ref="jobEditError"
    v-model="dialogStore.jobEditError.isDialog"
    :persistent="dialogStore.jobEditError.dialogData.isConfirmLoading"
    width="auto"
  >
    <JobEditErrorDialog
      v-bind="dialogStore.jobEditError.dialogData"
      @cancel-btn-click="dialogStore.handleJobEditErrorDialogCancelClick"
      @close-btn-click="dialogStore.close('jobEditError')"
      @try-again-btn-click="dialogStore.handleJobEditErrorDialogTryAgainClick"
    />
  </v-dialog>
  <v-dialog
    ref="splitToBase"
    v-model="dialogStore.splitToBase.isDialog"
    :persistent="dialogStore.splitToBase.dialogData.isConfirmLoading"
    width="auto"
  >
    <SplitToBaseDialog
      v-bind="dialogStore.splitToBase.dialogData"
      @cancel-btn-click="dialogStore.handleCancelClick($event, 'splitToBase')"
      @confirm-btn-click="dialogStore.handleSplitToBaseDialogConfirmClick"
    />
  </v-dialog>
  <v-dialog
    ref="editTimestamps"
    v-model="dialogStore.editTimestamps.isDialog"
    :persistent="dialogStore.editTimestamps.dialogData.isConfirmLoading"
    width="auto"
  >
    <EditTimestampsDialog
      v-bind="dialogStore.editTimestamps.dialogData"
      @cancel-btn-click="
        dialogStore.handleCancelClick($event, 'editTimestamps')
      "
      @confirm-btn-click="dialogStore.handleEditTimestampsDialogConfirmClick"
    />
  </v-dialog>
  <v-dialog
    ref="advancedSearchMultiSearch"
    v-model="dialogStore.advancedSearchMultiSearch.isDialog"
    :persistent="
      dialogStore.advancedSearchMultiSearch.dialogData.isConfirmLoading
    "
    width="auto"
  >
    <AdvancedSearchMultiSearchDialog
      v-bind="dialogStore.advancedSearchMultiSearch.dialogData"
      @cancel-btn-click="
        dialogStore.handleCancelClick($event, 'advancedSearchMultiSearch')
      "
      @confirm-btn-click="
        dialogStore.handleAdvancedSearchMultiSearchDialogConfirmClick
      "
    />
  </v-dialog>
  <v-dialog
    ref="savedFilters"
    v-model="dialogStore.savedFilters.isDialog"
    width="auto"
  >
    <SavedFiltersDialog
      v-bind="dialogStore.savedFilters.dialogData"
      @cancel-btn-click="dialogStore.handleCancelClick($event, 'savedFilters')"
    />
  </v-dialog>
  <v-dialog
    ref="savingCurrentSearch"
    v-model="dialogStore.savingCurrentSearch.isDialog"
    :persistent="dialogStore.savingCurrentSearch.dialogData.isConfirmLoading"
    width="auto"
  >
    <SavingCurrentSearchDialog
      v-bind="dialogStore.savingCurrentSearch.dialogData"
      @cancel-btn-click="
        dialogStore.handleCancelClick($event, 'savingCurrentSearch')
      "
      @confirm-btn-click="
        dialogStore.handleSavingCurrentSearchDialogConfirmClick
      "
    />
  </v-dialog>
  <v-dialog
    ref="uploadingFiles"
    v-model="dialogStore.uploadingFiles.isDialog"
    :persistent="dialogStore.uploadingFiles.dialogData.isConfirmLoading"
    width="auto"
  >
    <UploadingFilesDialog
      v-bind="dialogStore.uploadingFiles.dialogData"
      @cancel-btn-click="
        dialogStore.handleCancelClick($event, 'uploadingFiles')
      "
      @confirm-btn-click="dialogStore.handleUploadingFilesDialogConfirmClick"
    />
  </v-dialog>
  <v-dialog
    ref="detailedJobPhoto"
    v-model="dialogStore.detailedJobPhoto.isDialog"
    width="auto"
  >
    <DetailedJobPhotoDialog
      v-bind="dialogStore.detailedJobPhoto.dialogData"
      @cancel-btn-click="
        dialogStore.handleCancelClick($event, 'detailedJobPhoto')
      "
    />
  </v-dialog>
  <v-dialog
    ref="deleteJobPhoto"
    v-model="dialogStore.deleteJobPhoto.isDialog"
    :persistent="dialogStore.deleteJobPhoto.dialogData.isConfirmLoading"
    width="auto"
  >
    <DeleteJobPhotoDialog
      v-bind="dialogStore.deleteJobPhoto.dialogData"
      @cancel-btn-click="
        dialogStore.handleCancelClick($event, 'deleteJobPhoto')
      "
      @confirm-btn-click="dialogStore.handleDeleteJobPhotoDialogConfirmClick"
    />
  </v-dialog>
  <v-dialog
    ref="print"
    v-model="dialogStore.print.isDialog"
    :persistent="dialogStore.print.dialogData.isConfirmLoading"
    width="auto"
  >
    <PrintDialog
      v-bind="dialogStore.print.dialogData"
      @cancel-btn-click="dialogStore.handleCancelClick($event, 'print')"
      @confirm-btn-click="dialogStore.handlePrintDialogConfirmClick"
    />
  </v-dialog>
  <v-dialog
    ref="returnLabelsPrint"
    v-model="dialogStore.returnLabelsPrint.isDialog"
    :persistent="dialogStore.returnLabelsPrint.dialogData.isConfirmLoading"
    width="auto"
  >
    <ReturnLabelsPrintDialog
      v-bind="dialogStore.returnLabelsPrint.dialogData"
      @cancel-btn-click="
        dialogStore.handleCancelClick($event, 'returnLabelsPrint')
      "
      @confirm-btn-click="dialogStore.handleReturnLabelsPrintDialogConfirmClick"
    />
  </v-dialog>
  <v-dialog
    ref="newAirRoadUnpack"
    v-model="dialogStore.newAirRoadUnpack.isDialog"
    :persistent="dialogStore.newAirRoadUnpack.dialogData.isConfirmLoading"
    width="auto"
  >
    <NewAirRoadUnpackDialog
      v-bind="dialogStore.newAirRoadUnpack.dialogData"
      @cancel-btn-click="
        dialogStore.handleCancelClick($event, 'newAirRoadUnpack')
      "
      @confirm-btn-click="dialogStore.handleNewAirRoadUnpackDialogConfirmClick"
    />
  </v-dialog>
  <v-dialog
    ref="newMessageChat"
    v-model="dialogStore.newMessageChat.isDialog"
    :persistent="dialogStore.newMessageChat.dialogData.isConfirmLoading"
    width="auto"
  >
    <NewMessageChatDialog
      v-bind="dialogStore.newMessageChat.dialogData"
      @cancel-btn-click="
        dialogStore.handleCancelClick($event, 'newMessageChat')
      "
      @confirm-btn-click="dialogStore.handleNewMessageChatDialogConfirmClick"
    />
  </v-dialog>
  <v-dialog
    ref="kpiReportGenerate"
    v-model="dialogStore.kpiReportGenerate.isDialog"
    :persistent="dialogStore.kpiReportGenerate.dialogData.isConfirmLoading"
    width="auto"
  >
    <KpiReportGenerateDialog
      v-bind="dialogStore.kpiReportGenerate.dialogData"
      @cancel-btn-click="
        dialogStore.handleCancelClick($event, 'kpiReportGenerate')
      "
      @confirm-btn-click="dialogStore.handleKpiReportGenerateDialogConfirmClick"
    />
  </v-dialog>
  <v-dialog
    ref="driverReportGenerate"
    v-model="dialogStore.driverReportGenerate.isDialog"
    :persistent="dialogStore.driverReportGenerate.dialogData.isConfirmLoading"
    width="auto"
  >
    <DriverReportGenerateDialog
      v-bind="dialogStore.driverReportGenerate.dialogData"
      @cancel-btn-click="
        dialogStore.handleCancelClick($event, 'driverReportGenerate')
      "
      @confirm-btn-click="
        dialogStore.handleDriverReportGenerateDialogConfirmClick
      "
    />
  </v-dialog>
  <v-dialog
    ref="addAddress"
    v-model="dialogStore.addAddress.isDialog"
    :persistent="dialogStore.addAddress.dialogData.isConfirmLoading"
    width="auto"
  >
    <AddAddressDialog
      v-bind="dialogStore.addAddress.dialogData"
      @cancel-btn-click="dialogStore.handleCancelClick($event, 'addAddress')"
      @confirm-btn-click="dialogStore.handleAddAddressDialogConfirmClick"
    />
  </v-dialog>
  <v-dialog
    ref="addressBookAddress"
    v-model="dialogStore.addressBookAddress.isDialog"
    :persistent="dialogStore.addressBookAddress.dialogData.isConfirmLoading"
    width="auto"
  >
    <AddressBookAddressDialog
      v-bind="dialogStore.addressBookAddress.dialogData"
      @cancel-btn-click="
        dialogStore.handleCancelClick($event, 'addressBookAddress')
      "
      @confirm-btn-click="
        dialogStore.handleAddressBookAddressDialogConfirmClick
      "
    />
  </v-dialog>
  <v-dialog
    ref="addressBookDeleteAddress"
    v-model="dialogStore.addressBookDeleteAddress.isDialog"
    :persistent="
      dialogStore.addressBookDeleteAddress.dialogData.isConfirmLoading
    "
    width="auto"
  >
    <AddressBookDeleteAddressDialog
      v-bind="dialogStore.addressBookDeleteAddress.dialogData"
      @cancel-btn-click="
        dialogStore.handleCancelClick($event, 'addressBookDeleteAddress')
      "
      @confirm-btn-click="
        dialogStore.handleAddressBookDeleteAddressDialogConfirmClick
      "
    />
  </v-dialog>
  <v-dialog
    ref="saveJobDraft"
    v-model="dialogStore.saveJobDraft.isDialog"
    :persistent="dialogStore.saveJobDraft.dialogData.isConfirmLoading"
    width="auto"
  >
    <SaveJobDraftDialog
      v-bind="dialogStore.saveJobDraft.dialogData"
      @cancel-btn-click="dialogStore.handleCancelClick($event, 'saveJobDraft')"
      @confirm-btn-click="dialogStore.handleSaveJobDraftDialogConfirmClick"
    />
  </v-dialog>
  <v-dialog
    ref="confirmScrapBooking"
    v-model="dialogStore.confirmScrapBooking.isDialog"
    :persistent="dialogStore.confirmScrapBooking.dialogData.isConfirmLoading"
    width="auto"
  >
    <ConfirmScrapBookingDialog
      v-bind="dialogStore.confirmScrapBooking.dialogData"
      @cancel-btn-click="
        dialogStore.handleCancelClick($event, 'confirmScrapBooking')
      "
      @confirm-btn-click="
        dialogStore.handleConfirmScrapBookingDialogConfirmClick
      "
    />
  </v-dialog>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

</style>
